import React, { useState, useMemo, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import Layout from "../components/Layout";
import { EmptyState } from "../components/common/EmptyState";
import { useTurnstileContext } from "../hooks/useTurnstile";
import { useDebounce } from "../hooks/useDebounce";
import { formatDate } from "../utils/date.utils";
import { isValidUUID } from "../utils/url-validation";
import apiClient, { getApiUrl } from "../services/api";
import {
  ArticleListItem,
  ArticlesPageResponse,
  ArticlesViewFilters,
  ArticlesSchemeValue,
  SavedViewFilters,
  SavedViewItem,
} from "../services/grantsApi";
import {
  articleRoleLabels,
  articleRoleColors,
} from "../components/grants/shared/articleRoleLabels";
import { FilterBar } from "../components/grants/calls/filters/FilterBar";
import { FilterModal } from "../components/grants/calls/filters/FilterModal";
import { useFilterState } from "../components/grants/calls/filters/useFilterState";
import { articlesFilterRegistry } from "../components/grants/articles/filters/registry";
import SavedViewsBar from "../components/grants/saved-views/SavedViewsBar";
import { TurnstileErrorMessage } from "../components/grants/TurnstileErrorMessage";

/** Max number of call chips shown per article row before truncation */
const MAX_VISIBLE_CALL_CHIPS = 5;

/**
 * Backend origin for the article redirect proxy. Resolved once at module load
 * (depends only on the stable host) instead of per article-row render.
 */
const API_BASE_URL = getApiUrl();

/**
 * Default page size for the articles list. Local to this file by design — the
 * calls page (GrantsCalls.tsx) owns its own default and is maintained by another
 * owner, so we intentionally do NOT share a single import.
 */
const DEFAULT_ARTICLES_LIMIT = 50;

const ALLOWED_SORT_FIELDS = ["createdAt", "updatedAt"] as const;
type SortField = (typeof ALLOWED_SORT_FIELDS)[number];

/**
 * External link icon — used in article title cells.
 *
 * Kept local: there is no shared `ExternalLinkIcon` in
 * `components/common/`. The same SVG path is inlined ad-hoc across several
 * grants components; extracting a shared icon component is out of scope here.
 */
const ExternalLinkIcon: React.FC = () => (
  <svg
    className="inline-block w-3.5 h-3.5 ml-1 text-neutral-400 flex-shrink-0"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
    />
  </svg>
);

/**
 * A chip showing one linked call (nabor) for an article row.
 * Displays call title (link to /grants/calls/:id), articleRole badge,
 * "Ręcznie dodane" badge if MANUAL, and program/project name.
 */
export const CallChip: React.FC<{ call: ArticleListItem["calls"][number] }> = ({
  call,
}) => {
  const roleLabel = articleRoleLabels[call.articleRole] ?? call.articleRole;
  const roleColor =
    articleRoleColors[call.articleRole] ?? articleRoleColors.OTHER;

  return (
    // The whole chip (not just the title) links to the call detail page.
    // NOTE: call.callTitle may carry an invisible zero-width watermark token
    // (ADR-087) — visually identical but byte-different per userId+day. A
    // future toHaveAccessibleName(...) / title snapshot test must strip
    // zero-width chars before comparing, or it will be flaky.
    <Link
      to={`/grants/calls/${call.callId}`}
      className="group flex flex-wrap items-center gap-1 p-2 rounded-md bg-neutral-50 border border-neutral-200 text-xs hover:bg-neutral-100 hover:border-neutral-300 transition-colors"
      title={call.callTitle}
      aria-label={`Nabór: ${call.callTitle}`}
    >
      {/* Call title */}
      <span className="font-medium text-primary-700 truncate max-w-[200px] group-hover:text-primary-900 group-hover:underline">
        {call.callTitle}
      </span>

      {/* articleRole badge (per relation, not per article) */}
      <span
        className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${roleColor}`}
        title={roleLabel}
      >
        {roleLabel}
      </span>

      {/* "Ręcznie dodane" badge for MANUAL mappings */}
      {call.mappingType === "MANUAL" && (
        <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Ręcznie dodane
        </span>
      )}

      {/* Program / project context */}
      <span className="text-neutral-500 truncate">
        {call.programName}
        {call.projectName ? ` / ${call.projectName}` : ""}
      </span>
    </Link>
  );
};

/**
 * Client-facing article-centric list page (/grants/articles).
 * One row = one article; nabory shown as chips in "Nabory" column.
 *
 * Filters use the SAME plugin-based mechanism as /grants/calls
 * (useFilterState + FilterBar + FilterModal + MultiSelectDropdown) via the
 * articles filter registry (reused calls plugins + ArticleRole + ArticlesScheme).
 * System views: KFS · BUR · Pozostałe · Wszystkie (SavedViewsBar, viewType="articles").
 *
 * Access: grants:calls module (same as SavedViews).
 * Guard: CombinedAuthGuard + ModuleGuard (handled by ProtectedRoute in App.tsx).
 * Note: article list is global (not tenant-owned, per ADR-010) — no TenantGuard.
 *
 * @see docs/specifications/2026-06-08-articles-filters-unification/spec.md
 */
const GrantsArticlesTab: React.FC = () => {
  const { isCookieReady } = useTurnstileContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);

  // Plugin-based filter state (reads/writes URL params for all articles filters)
  const {
    filterValues,
    toApiParams,
    toSavedViewFilters,
    applySavedView,
    advancedFiltersCount,
    hasActiveFilters,
  } = useFilterState(articlesFilterRegistry);

  // ---- Page / Limit / Sort / View: non-filter URL params ----

  const page = useMemo(() => {
    const p = searchParams.get("page");
    return p ? parseInt(p, 10) : 1;
  }, [searchParams]);

  const limit = useMemo(() => {
    const l = searchParams.get("limit");
    return l ? parseInt(l, 10) : DEFAULT_ARTICLES_LIMIT;
  }, [searchParams]);

  const sortBy = useMemo((): SortField => {
    const s = searchParams.get("sortBy");
    return ALLOWED_SORT_FIELDS.includes(s as SortField)
      ? (s as SortField)
      : "updatedAt";
  }, [searchParams]);

  const sortOrder = useMemo((): "ASC" | "DESC" => {
    const o = searchParams.get("sortOrder");
    return o === "ASC" ? "ASC" : "DESC";
  }, [searchParams]);

  const activeViewId = useMemo(() => {
    const v = searchParams.get("view");
    return v && isValidUUID(v) ? v : null;
  }, [searchParams]);

  // Current scheme (incl. 'pozostale') drives the dedicated empty state.
  const currentScheme = filterValues.scheme as ArticlesSchemeValue | undefined;

  // ---- URL helpers (preserve filter params from plugin system) ----

  /**
   * Build URL params from the plugin registry plus non-filter params.
   * Optionally override filter values (for applying a saved view) and page/view.
   */
  const buildUrlParams = useCallback(
    (options?: {
      values?: Record<string, unknown>;
      page?: number;
      viewId?: string | null;
      clearSort?: boolean;
    }) => {
      const params = new URLSearchParams();
      const values = options?.values ?? filterValues;
      for (const plugin of articlesFilterRegistry) {
        const value = values[plugin.key] ?? plugin.defaultValue;
        if (plugin.isActive(value)) {
          plugin.toUrlParams(value, params);
        }
      }

      const newPage = options?.page ?? page;
      if (newPage > 1) params.set("page", newPage.toString());
      if (limit !== DEFAULT_ARTICLES_LIMIT)
        params.set("limit", limit.toString());

      if (!options?.clearSort) {
        if (sortBy !== "updatedAt") params.set("sortBy", sortBy);
        if (sortOrder !== "DESC") params.set("sortOrder", sortOrder);
      }

      // View param: null = clear, undefined = preserve, string = set
      const viewId = options?.viewId;
      if (viewId === null) {
        // explicitly cleared
      } else if (viewId !== undefined) {
        params.set("view", viewId);
      } else {
        const existing = searchParams.get("view");
        if (existing && isValidUUID(existing)) params.set("view", existing);
      }

      return params;
    },
    [filterValues, page, limit, sortBy, sortOrder, searchParams],
  );

  // Single filter change: reset page to 1, preserve limit/sort, drop view param.
  const handleFilterChange = useCallback(
    (key: string, value: unknown) => {
      const newValues = { ...filterValues, [key]: value };
      const params = buildUrlParams({
        values: newValues,
        page: 1,
        viewId: null,
      });
      setSearchParams(params, { replace: true });
    },
    [filterValues, buildUrlParams, setSearchParams],
  );

  // Advanced modal apply: merge draft into current filters, reset page, drop view.
  const handleAdvancedFiltersApply = useCallback(
    (draftValues: Record<string, unknown>) => {
      const merged = { ...filterValues, ...draftValues };
      const params = buildUrlParams({
        values: merged,
        page: 1,
        viewId: null,
      });
      setSearchParams(params, { replace: true });
    },
    [filterValues, buildUrlParams, setSearchParams],
  );

  // Clear all filters: reset to empty, preserve only limit.
  const handleClearFilters = useCallback(() => {
    const params = new URLSearchParams();
    if (limit !== DEFAULT_ARTICLES_LIMIT) params.set("limit", limit.toString());
    setSearchParams(params, { replace: true });
  }, [limit, setSearchParams]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setSearchParams(buildUrlParams({ page: newPage }), { replace: true });
    },
    [buildUrlParams, setSearchParams],
  );

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      const params = buildUrlParams({ page: 1 });
      if (newLimit !== DEFAULT_ARTICLES_LIMIT)
        params.set("limit", String(newLimit));
      else params.delete("limit");
      setSearchParams(params, { replace: true });
    },
    [buildUrlParams, setSearchParams],
  );

  const handleSort = useCallback(
    (field: SortField) => {
      const newOrder: "ASC" | "DESC" =
        sortBy === field ? (sortOrder === "DESC" ? "ASC" : "DESC") : "DESC";
      const params = buildUrlParams({ page: 1, clearSort: true });
      if (field !== "updatedAt") params.set("sortBy", field);
      if (newOrder !== "DESC") params.set("sortOrder", newOrder);
      setSearchParams(params, { replace: true });
    },
    [sortBy, sortOrder, buildUrlParams, setSearchParams],
  );

  const ariaSortFor = (
    field: SortField,
  ): "ascending" | "descending" | "none" =>
    sortBy === field
      ? sortOrder === "ASC"
        ? "ascending"
        : "descending"
      : "none";

  // Keyboard activation for sortable column headers (Enter / Space → handleSort).
  const handleSortKeyDown = useCallback(
    (field: SortField) => (e: React.KeyboardEvent<HTMLTableCellElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleSort(field);
      }
    },
    [handleSort],
  );

  // ---- Saved views handlers ----

  // Apply a saved view: set filter values from the view + ?view=<id>.
  const handleViewSelect = useCallback(
    (view: SavedViewItem) => {
      const viewFilters = view.filters as ArticlesViewFilters;
      // Reuse plugin deserialization for a single atomic navigation.
      applySavedView(viewFilters as unknown as SavedViewFilters);
      const params = new URLSearchParams();
      for (const plugin of articlesFilterRegistry) {
        const value = plugin.fromSavedView(
          viewFilters as unknown as SavedViewFilters,
        );
        if (plugin.isActive(value)) {
          plugin.toUrlParams(value, params);
        }
      }
      if (limit !== DEFAULT_ARTICLES_LIMIT)
        params.set("limit", limit.toString());

      // Restore sort from the view if persisted (sortBy/sortOrder are not part
      // of the typed ArticlesViewFilters bag, so read defensively). Only valid
      // enum values are applied; defaults are omitted from the URL.
      const rawView = viewFilters as Record<string, unknown>;
      const viewSortBy = rawView.sortBy;
      const viewSortOrder = rawView.sortOrder;
      if (
        typeof viewSortBy === "string" &&
        ALLOWED_SORT_FIELDS.includes(viewSortBy as SortField) &&
        viewSortBy !== "updatedAt"
      ) {
        params.set("sortBy", viewSortBy);
      }
      if (viewSortOrder === "ASC") {
        params.set("sortOrder", "ASC");
      }

      params.set("view", view.id);
      setSearchParams(params, { replace: true });
    },
    [limit, applySavedView, setSearchParams],
  );

  // "Wszystkie": clear all filters + view (and scheme).
  const handleClearView = useCallback(() => {
    const params = new URLSearchParams();
    if (limit !== DEFAULT_ARTICLES_LIMIT) params.set("limit", limit.toString());
    setSearchParams(params, { replace: true });
  }, [limit, setSearchParams]);

  // Scheme preset (KFS/BUR/Pozostałe) or clear: preserve other filters, drop view.
  const handleSchemeChange = useCallback(
    (scheme: ArticlesSchemeValue | undefined) => {
      const params = new URLSearchParams(searchParams);
      params.delete("view");
      params.delete("page");
      if (scheme !== undefined) {
        params.set("scheme", scheme);
      } else {
        params.delete("scheme");
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // ---- Debounced filter values for API ----
  const debouncedFilterValues = useDebounce(filterValues, 300);

  // ---- Data fetching ----
  const { data, isLoading, isFetching, error, refetch } =
    useQuery<ArticlesPageResponse>({
      queryKey: [
        "grants-articles",
        debouncedFilterValues,
        page,
        limit,
        sortBy,
        sortOrder,
      ],
      enabled: isCookieReady,
      placeholderData: keepPreviousData,
      queryFn: async () => {
        // Build API params from the plugin registry (scheme, programIds,
        // projectIds, operatorIds, region, districts, barometer*, articleRoles,
        // search), then append pagination + sort.
        const params = toApiParams();
        params.set("sortBy", sortBy);
        params.set("sortOrder", sortOrder);
        params.set("page", String(page));
        params.set("limit", String(limit));
        const { data } = await apiClient.get<ArticlesPageResponse>(
          `/grants/articles?${params.toString()}`,
        );
        return data;
      },
    });

  // Program map (UUID -> name) for auto-suggest view name in SavedViewsBar.
  const programMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    data?.programCounts?.forEach((p) => {
      if (p.id && p.name) map[p.id] = p.name;
    });
    return map;
  }, [data?.programCounts]);

  // Current filters serialized for SavedViewsBar create/dirty detection.
  const currentSavedViewFilters = useMemo<ArticlesViewFilters>(
    () => toSavedViewFilters() as unknown as ArticlesViewFilters,
    [toSavedViewFilters],
  );

  // Region facet: calls RegionFilter reads `facets.regions` (slug list).
  const regionSlugs = useMemo<string[]>(
    () => (data?.regionCounts ?? []).map((r) => r.slug),
    [data?.regionCounts],
  );

  const effectivelyLoading = isLoading || !isCookieReady;

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Artykuły</h1>
          <p className="mt-1 text-sm text-neutral-600">
            Lista artykułów aktualności KFS, BUR oraz pozostałych źródeł,
            posortowanych po dacie aktualizacji
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
          {/* Saved views bar (system presets KFS · BUR · Pozostałe · Wszystkie + user views) */}
          <SavedViewsBar
            viewType="articles"
            currentUrlParams={searchParams}
            activeViewId={activeViewId}
            onViewSelect={handleViewSelect}
            onClearView={handleClearView}
            onViewNotFound={handleClearView}
            onSchemeChange={(scheme) =>
              handleSchemeChange(scheme as ArticlesSchemeValue | undefined)
            }
            currentFilters={currentSavedViewFilters}
            programMap={programMap}
          />

          <div className="p-2">
            {/* Plugin-based filter bar (articles registry) */}
            <FilterBar
              registry={articlesFilterRegistry}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onClearAll={handleClearFilters}
              onAdvancedFiltersClick={() => setIsAdvancedFiltersOpen(true)}
              advancedFiltersCount={advancedFiltersCount}
              hasActiveFilters={hasActiveFilters}
              isLoading={effectivelyLoading && !data}
              isFetching={isFetching && !!data}
              totalCount={data?.total}
              facets={{
                programCounts: data?.programCounts,
                projectCounts: data?.projectCounts,
                operatorCounts: data?.operatorCounts,
                regions: regionSlugs,
                regionCounts: data?.regionCounts,
                districtCounts: data?.districtCounts,
                articleRoleCounts: data?.articleRoleCounts,
              }}
            />

            {/* Plugin-based advanced filters modal (Barometr) */}
            <FilterModal
              isOpen={isAdvancedFiltersOpen}
              registry={articlesFilterRegistry}
              filterValues={filterValues}
              facets={{}}
              onApply={handleAdvancedFiltersApply}
              onClose={() => setIsAdvancedFiltersOpen(false)}
            />

            {/* Loading state */}
            {effectivelyLoading && !data ? (
              <div
                className="py-12 text-center"
                role="status"
                aria-live="polite"
              >
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
                <p className="mt-2 text-sm text-neutral-600">
                  Ładowanie artykułów...
                </p>
              </div>
            ) : error ? (
              <div className="py-12 text-center" role="alert">
                <div className="text-red-600 mb-4">
                  <svg
                    className="h-12 w-12 mx-auto mb-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <p className="font-medium">
                    Błąd podczas pobierania artykułów
                  </p>
                  <TurnstileErrorMessage error={error} />
                </div>
                <button
                  onClick={() => refetch()}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Spróbuj ponownie
                </button>
              </div>
            ) : (
              <>
                {/* Results count */}
                <div className="mt-4 mb-4 flex items-center justify-between">
                  <p
                    className="text-sm text-neutral-600"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    {data?.total !== undefined
                      ? `${data.total} ${
                          data.total === 1
                            ? "artykuł"
                            : data.total < 5
                              ? "artykuły"
                              : "artykułów"
                        }`
                      : ""}
                    {isFetching && (
                      <span className="ml-2 inline-flex items-center">
                        <span className="animate-spin h-3 w-3 border-2 border-primary-600 border-t-transparent rounded-full mr-1" />
                        <span className="text-neutral-400">
                          aktualizacja...
                        </span>
                      </span>
                    )}
                  </p>
                  <select
                    value={limit}
                    onChange={(e) =>
                      handleLimitChange(parseInt(e.target.value, 10))
                    }
                    className="px-3 py-1 text-sm border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    aria-label="Liczba artykułów na stronę"
                  >
                    <option value={25}>25 na stronę</option>
                    <option value={50}>50 na stronę</option>
                    <option value={100}>100 na stronę</option>
                  </select>
                </div>

                {/* Empty states */}
                {data?.data?.length === 0 ? (
                  currentScheme === "pozostale" ? (
                    /* Dedicated "Pozostałe" empty state — semantic view, no "clear filters". */
                    <EmptyState
                      title="Brak artykułów nieprzypisanych"
                      description="Wszystkie artykuły są obecnie powiązane z programem KFS lub BUR. Pozostałe to artykuły bez przypisanego programu (źródło bez projektu lub jeszcze nieprzetworzone)."
                    />
                  ) : hasActiveFilters ? (
                    <EmptyState
                      title="Brak artykułów dla filtrów"
                      description="Nie znaleziono artykułów pasujących do wybranych kryteriów."
                      action={{
                        label: "Wyczyść filtry",
                        onClick: handleClearFilters,
                      }}
                    />
                  ) : (
                    <EmptyState
                      title="Brak artykułów w systemie"
                      description="Artykuły pojawią się automatycznie po ich wykryciu przez system."
                    />
                  )
                ) : (
                  /* Articles table */
                  <div className="overflow-x-auto -mx-4">
                    <table className="min-w-full divide-y divide-neutral-200">
                      <thead className="bg-neutral-50">
                        <tr>
                          <th
                            scope="col"
                            tabIndex={0}
                            aria-sort={ariaSortFor("createdAt")}
                            className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-neutral-100 select-none transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 ${
                              sortBy === "createdAt"
                                ? "text-neutral-700 bg-neutral-100"
                                : "text-neutral-500"
                            }`}
                            onClick={() => handleSort("createdAt")}
                            onKeyDown={handleSortKeyDown("createdAt")}
                          >
                            Dodano
                            {sortBy === "createdAt" && (
                              <span className="ml-1" aria-hidden="true">
                                {sortOrder === "ASC" ? "↑" : "↓"}
                              </span>
                            )}
                          </th>
                          <th
                            scope="col"
                            tabIndex={0}
                            aria-sort={ariaSortFor("updatedAt")}
                            className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-neutral-100 select-none transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 ${
                              sortBy === "updatedAt"
                                ? "text-neutral-700 bg-neutral-100"
                                : "text-neutral-500"
                            }`}
                            onClick={() => handleSort("updatedAt")}
                            onKeyDown={handleSortKeyDown("updatedAt")}
                          >
                            Zaktualizowano
                            {sortBy === "updatedAt" && (
                              <span className="ml-1" aria-hidden="true">
                                {sortOrder === "ASC" ? "↑" : "↓"}
                              </span>
                            )}
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider"
                          >
                            Tytuł
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider"
                          >
                            Nabory
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-neutral-100">
                        {data?.data?.map((article) => (
                          <ArticleRow
                            key={article.articleId}
                            article={article}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {data && data.totalPages > 1 && (
                  <nav
                    className="mt-6 flex items-center justify-between"
                    role="navigation"
                    aria-label="Paginacja artykułów"
                  >
                    <button
                      onClick={() => handlePageChange(data.page - 1)}
                      disabled={data.page <= 1}
                      aria-label="Przejdź do poprzedniej strony"
                      className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    >
                      Poprzednia
                    </button>
                    <span
                      className="text-sm text-neutral-600"
                      aria-current="page"
                    >
                      Strona {data.page} z {data.totalPages}
                    </span>
                    <button
                      onClick={() => handlePageChange(data.page + 1)}
                      disabled={data.page >= data.totalPages}
                      aria-label="Przejdź do następnej strony"
                      className="px-4 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-primary-500 focus:outline-none"
                    >
                      Następna
                    </button>
                  </nav>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

/**
 * One table row for an article.
 * Title: external link (target="_blank" rel="noopener noreferrer") + icon.
 * Row is NOT clickable (per spec: wiersz NIE klikalny).
 *
 * Anti-scraping: the list API never returns the raw external URL. The link
 * points at the public redirect proxy (GET /grants/articles/:id/redirect),
 * built here from the article id, so bots cannot harvest the whole link
 * database from the list response.
 */
export const ArticleRow: React.FC<{ article: ArticleListItem }> = ({
  article,
}) => {
  const redirectHref = isValidUUID(article.articleId)
    ? `${API_BASE_URL}/grants/articles/${article.articleId}/redirect`
    : null;

  // Hover tooltip shows the source domain (where the link leads) so the user can
  // still vet the source — the full URL is no longer sent to the client.
  const titleLink = redirectHref ? (
    <a
      href={redirectHref}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-0.5 text-sm font-medium text-primary-700 hover:text-primary-900 hover:underline"
      title={article.sourceDomain || article.title || undefined}
    >
      {article.title || "(bez tytułu)"}
      <ExternalLinkIcon />
      <span className="sr-only">(otwiera się w nowej karcie)</span>
    </a>
  ) : (
    <span className="text-sm text-neutral-700">
      {article.title || "(bez tytułu)"}
    </span>
  );

  return (
    <tr className="hover:bg-neutral-50 transition-colors">
      {/* Created at */}
      <td className="px-4 py-3 text-sm text-neutral-500 whitespace-nowrap">
        {formatDate(article.createdAt)}
      </td>

      {/* Updated at */}
      <td className="px-4 py-3 text-sm text-neutral-500 whitespace-nowrap">
        {formatDate(article.updatedAt)}
      </td>

      {/* Title column */}
      <td className="px-4 py-3 max-w-xs">{titleLink}</td>

      {/* Calls (chips) column */}
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          {article.calls.slice(0, MAX_VISIBLE_CALL_CHIPS).map((call) => (
            <CallChip key={call.mappingId} call={call} />
          ))}
          {article.calls.length > MAX_VISIBLE_CALL_CHIPS && (
            <span className="text-xs text-neutral-400 pl-2">
              +{article.calls.length - MAX_VISIBLE_CALL_CHIPS} więcej
            </span>
          )}
          {article.calls.length === 0 && (
            <span className="text-xs text-neutral-400 pl-2">
              Brak powiązanych naborów
            </span>
          )}
        </div>
      </td>
    </tr>
  );
};

export default GrantsArticlesTab;
